import { Button, Space, Table, Typography, Flex } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { StyledButton } from "../../Common/Components";
import ConfirmationModal from "../../Common/Components/ConfirmationModal";
import { PaginationComponent } from "../../Common/Pagination/PaginationComponent";
import PermissionCheck from "../../Common/PermissionCheck";
import { useDeleteLeague } from "../../entities/api";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useGetOrgData } from "../../hooks/useGetOrgData";
import { useHandlePagination } from "../../hooks/useHandlePagination";
import { useNotification } from "~hooks/useNotification";
import useGetCurrentUserPermissionScope from "~hooks/useGetCurrentUserPermissionScope";
import { ImageListView } from "~components/Images";
import { ImageTypes } from "~enums/imageTypes";

const columnsone = ({ customerId, sport, organizationId, formatMessage, handleOpenConfirmation }) => {
  return [
    {
      dataIndex: "images",
      render: (images) => <ImageListView imageType={ImageTypes.Logo} image={images?.find((image) => image.imageType === ImageTypes.Logo)} />,
      width: 1,
    },
    {
      title: formatMessage({
        id: "name.local.full",
        defaultMessage: "Local Full Name",
      }),
      dataIndex: "nameLocal",
    },
    {
      title: formatMessage({
        id: "regionType",
        defaultMessage: "Region Type",
      }),
      dataIndex: "regionType",
    },
    {
      render: (_, record) => (
        <div style={{ display: "flex" }}>
          <PermissionCheck permissionKeys={[PermissionNames.LEAGUE]} permissionOptions={[PermissionOperations.UPDATE]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/leagues/manage/${record.leagueId}/`}>
              <StyledButton type={"text"}>
                {formatMessage({
                  id: "manage",
                  defaultMessage: "Manage",
                })}
              </StyledButton>
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionNames.LEAGUE]} permissionOptions={[PermissionOperations.DELETE]}>
            <StyledButton
              type={"text"}
              onClick={() => {
                handleOpenConfirmation(record);
              }}
            >
              <i data-testid="delete-icon" className={`fas fa-trash-can`}/>
            </StyledButton>
          </PermissionCheck>
        </div>
      ),
      width: 1,
    },
  ];
};

const LeaguesList = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId, customerId } = useGetOrgData();
  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination();
  const [selected, setSelected] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { mutate: deleteFn, isPending, isSuccess } = useDeleteLeague();
  const { data: scopeData, isLoading } = useGetCurrentUserPermissionScope();
  const { scope, resources } = scopeData || {};

  const data = useMemo(() => {
    const leaguesData = resources?.leagues || {};

    return (scope?.leagueTree || [])
      .filter(({ leagueId }) => leagueId && leagueId in leaguesData)
      .map(({ leagueId }) => ({ ...leaguesData[leagueId] }));
  }, [scope?.leagueTree, resources?.leagues]);

  const notification = useNotification();

  const handleOpenConfirmation = useCallback(
    (record) => {
      setSelected(record);
      setShowConfirmation(true);
    },
    [setSelected, setShowConfirmation],
  );

  useEffect(() => {
    if (isSuccess) {
      notification.success({
        message: `${selected.nameLocal} ${formatMessage({
          id: "league.delete.success",
          defaultMessage: "League deleted successfully",
        })}`,
        placement: "bottomRight",
      });
    }
  }, [isSuccess, selected]);

  const handleDelete = useCallback(() => {
    deleteFn(selected?.leagueId);
  }, [deleteFn, selected?.leagueId]);

  return (
    <Flex vertical gap={8}>
      <Flex align="center" justify="space-between">
        <PermissionCheck permissionKeys={[PermissionNames.LEAGUE]} permissionOptions={[PermissionOperations.DELETE]}>
          <ConfirmationModal
            title="Delete Competition"
            setIsModalOpen={setShowConfirmation}
            isModalOpen={showConfirmation}
            handleOk={handleDelete}
            handleCancel={() => {
              setSelected({});
            }}
            okText="Delete"
          >
            <div>{`You are about to delete ${selected.nameLocal}. Is this OK?`}</div>
          </ConfirmationModal>
        </PermissionCheck>
        <Typography.Title level={4}>Leagues</Typography.Title>
        <Space>
          <PermissionCheck permissionKeys={[PermissionNames.LEAGUE]} permissionOptions={[PermissionOperations.CREATE]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/leagues/manage/`}>
              <Button type="primary" icon={<i className="fa-solid fa-plus mr-1"></i>}>
                League
              </Button>
            </Link>
          </PermissionCheck>
        </Space>
      </Flex>
      <Table
        loading={isLoading || isPending}
        size={"small"}
        columns={columnsone({
          customerId,
          sport,
          organizationId,
          formatMessage,
          handleOpenConfirmation,
        })}
        rowKey={"leagueId"}
        dataSource={data}
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions,
          showQuickJumper: false,
          total: data.length,
          position: ["bottomCenter"],
          onChange: handlePaginationChange,
          showTotal: (total, range) => (
            <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range}/>
          ),
        }}
      />
    </Flex>
  );
};

export default LeaguesList;
